/**
 * Usuario de acceso del Portal de Permisionarios.
 *
 * El permisionario es externo y muchas veces no tiene un correo que use a
 * diario, así que pedirle uno para poder entrar dejaba el alta parada. Entra
 * con un USUARIO fácil de recordar —"TransportesRalla"— y su correo personal
 * lo captura él mismo, después, para notificaciones.
 *
 * better-auth inicia sesión con `email`, y esa columna es única y obligatoria.
 * En vez de meterle el plugin de username —config, tablas y migración— el
 * usuario se guarda como un correo sintético `transportesralla@portal.local`:
 * la unicidad la sigue dando el índice de `user.email` y no hay nada nuevo que
 * mantener.
 *
 * `portal.local` NO existe como dominio y eso es a propósito: nada le puede
 * mandar correo por error a una dirección que solo sirve para iniciar sesión.
 */

export const PORTAL_LOGIN_DOMAIN = "portal.local";

/**
 * Normaliza lo que se teclea a la forma con la que se guarda: minúsculas, sin
 * acentos y sin nada que no sea letra o número.
 *
 * Sin esto, "Transportes Ralla" y "TransportesRalla" son dos cuentas distintas
 * que el permisionario cree que son la misma, y la que no existe responde
 * "credenciales inválidas".
 */
export function toPortalUsername(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

/** El correo sintético con el que better-auth resuelve el inicio de sesión. */
export function toPortalLoginEmail(raw: string): string {
  return `${toPortalUsername(raw)}@${PORTAL_LOGIN_DOMAIN}`;
}

/**
 * Lo que se le enseña a la persona: el usuario si es una cuenta del portal, y
 * el correo tal cual si es una cuenta vieja creada con uno real.
 */
export function portalLoginLabel(email: string): string {
  return email.endsWith(`@${PORTAL_LOGIN_DOMAIN}`)
    ? email.split("@")[0]
    : email;
}

/**
 * Con qué entra: si trae arroba es un correo y va tal cual; si no, es un
 * usuario del portal y se convierte al correo sintético.
 *
 * Lo usa la pantalla de inicio de sesión, que es una sola para el personal
 * interno y para los permisionarios.
 */
export function resolveLoginIdentifier(input: string): string {
  const value = input.trim();
  return value.includes("@") ? value.toLowerCase() : toPortalLoginEmail(value);
}
